import { UserRole } from "@/utils/enum/user-role";

export interface AccessControlResult {
  hasAccess: boolean;
  redirectPath?: string;
  accessDeniedType?: "adminRequired" | "doctorRequired" | "loginRequired";
}

/**
 * 사용자 역할과 경로에 대한 접근 권한을 체크합니다.
 * @param userRole 사용자 역할
 * @param pathname 현재 경로
 * @returns 접근 권한 체크 결과
 */
export function checkAccessPermission(
  userRole: string | null,
  pathname: string
): AccessControlResult {
  const isAdminPath = pathname.startsWith("/admin");
  const isDoctorHomePath = pathname.startsWith("/home");
  const isLoginPath = pathname === "/";

  // 로그인 페이지는 모든 사용자가 접근 가능
  if (isLoginPath) {
    return { hasAccess: true };
  }

  // 역할 정보가 없는 경우
  if (!userRole) {
    if (isAdminPath || isDoctorHomePath) {
      return {
        hasAccess: false,
        redirectPath: "/",
        accessDeniedType: "loginRequired",
      };
    }
    return { hasAccess: true };
  }

  // 의사가 관리자 페이지에 접근 시도
  if (isAdminPath && userRole === "Doctor") {
    return {
      hasAccess: false,
      redirectPath: "/home",
      accessDeniedType: "adminRequired",
    };
  }

  // 관리자/슈퍼관리자가 의사 페이지에 접근 시도
  if (isDoctorHomePath && (userRole === "SuperAdmin" || userRole === "Admin")) {
    return {
      hasAccess: false,
      redirectPath: "/admin/home",
      accessDeniedType: "doctorRequired",
    };
  }

  // 접근 허용
  return { hasAccess: true };
}

/**
 * NextRequest에서 쿠키의 사용자 역할을 가져옵니다.
 * @param request NextRequest 객체
 * @returns 사용자 역할 또는 null
 */
export function getUserRoleFromRequest(request: any): string | null {
  return request.cookies.get("user-role")?.value || null;
}

/**
 * 접근 권한 체크 결과를 기반으로 리다이렉트 URL을 생성합니다.
 * @param baseUrl 기본 URL
 * @param result 접근 권한 체크 결과
 * @returns 리다이렉트 URL
 */
export function createRedirectUrl(baseUrl: any, result: AccessControlResult): string {
  const url = baseUrl.clone();
  url.pathname = result.redirectPath!;
  if (result.accessDeniedType) {
    url.searchParams.set("accessDenied", result.accessDeniedType);
  }
  return url.toString();
}
